import './mask-field-list-ref.scss'

import { defineComponent, PropType } from 'vue'
import { first } from 'rxjs/operators';
import _ from 'lodash';
import { faTrash } from '@fortawesome/free-solid-svg-icons'

import { EntityField, EntityFieldStatus } from '@skinsoft/types';

import { reactiveVueMixin } from '@skinsoft/reactive-vue';
import { EntitySelect } from '@skinsoft/ui';

export const MaskFieldListRef = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sEntitySelect: EntitySelect,
    },
    props: {
        entityField: {
            type: Object as PropType<EntityField>,
            required: true,
        },
        fieldValue: {
            type: Array as PropType<any[] | null>,
        },
        editing: {
            type: Boolean,
        },
        getStatus: {
            type: Function as PropType<(entityField: EntityField) => EntityFieldStatus>,
            required: true,
        },
        dataCy: {
            type: String,
        },
    },
    template: require('./mask-field-list-ref.html'),
    data() {
        return {
            fieldLabel: "",

            trashIcon: faTrash,
        }
    },
    computed: {
        values(): any[] {
            return this.fieldValue ?? []
        },
    },
    methods: {
        sanitizeValue(value: any): string {
            return _.get(value, 'ref') ?? value
        },

        onEntitySelected(entity: any, index) {
            const path = `${this.entityField.path}.${index}`

            this.$emit("value-change", {
                abstractEntity: this.entityField.abstractEntity,
                fieldPath: path,
                value: entity.id,
            })
        },

        removeEntry(index: number) {
            // browser version required for toSpliced to work are still relativelly recent, wait a little bit longer
            // before using it
            //const updatedValues = this.values.toSpliced(index, 1)
            const updatedValues = this.values
            updatedValues.splice(index, 1)

            this.$emit("value-change", {
                abstractEntity: this.entityField.abstractEntity,
                fieldPath: this.entityField.path,
                value: updatedValues,
            })
        },

        thesaurusIdentifierIfNeeded(entityField: EntityField) {
            if (entityField.type.type === "ThesaurusTerm") {
                return entityField.type.thesaurusIdentifier
            }

            if (entityField.type.entityFieldType?.type === "ThesaurusTerm") {
                return entityField.type.entityFieldType.thesaurusIdentifier
            }

            return null
        },

        fetchLabel(key: string) {
            let translationKey: string = `field.${key}`
            if (this.entityField.entityFields
                && this.entityField.entityFields.length > 0) {
                translationKey += ".complex"
            }
            this.subscribeTo(
                this.$translationDataStore.getMessage(translationKey, this.getAppLocale()).pipe(first()),
                translation => {
                    this.fieldLabel = translation
                },
                'translation-subscription'
            )
        },

        isReadonly(entityField: EntityField) {
            return !this.editing || entityField.isReadOnly || this.getStatus(entityField).disabled
        },

        infoMessage(entityField: EntityField): string | undefined {
            return this.getStatus(entityField).disabledMessage
        },

        errorMessage(entityField: EntityField): string | undefined {
            return this.getStatus(entityField).errorMessage
        },

        hasError(entityField: EntityField) {
            return this.getStatus(entityField).error
        },

        getDataCy(index?: number): String {
            let res: string = this.entityField.path
            if(this.dataCy) {
                res = this.dataCy
            }
            if(index !== undefined && index !== null) {
                res = `${res}.${index}`
            }

            return res
        },

        getKey(index: number) {
            const value = this.values[index]

            if(value) {
                if(value instanceof String || typeof value === 'string') {
                    return this.entityField.name + '-' + index + '-' + value
                } else {
                    return this.entityField.name + '-' + index + '-' + JSON.stringify(value)
                }
            }

            return this.entityField.name + '-' + index
        },
    },
})
